import {
  type AppLocale,
  normalizeLocale,
} from "@airange/shared/i18n-exam-admin";
import { type AuthLoginSource } from "@airange/shared/auth";
import type { AuthMode } from "@airange/shared/dicts";
import { createSessionStore, createSessionValueStore } from "@airange/shared/session";
import { getStoredAppLocale, setStoredAppLocale } from "@airange/shared/locale-storage";
import type { UserRole, UserSession } from "./auth-contract";
import { readUserRoleKey, readUserRoleKeys } from "./exam-user-role-enums";

const ACCESS_TOKEN_KEY = "exam.admin.access_token";
const USER_KEY = "exam.admin.user";
const LOCALE_KEY = "exam.admin.locale";
const AUTH_SOURCE_KEY = "exam.admin.auth_source";

export type { AuthLoginSource } from "@airange/shared/auth";
export type { AuthMode } from "@airange/shared/dicts";

export const PORTAL_ROLES: UserRole[] = ["admin", "question_editor", "exam_manager"];
export type CurrentUserRoleSource = Pick<UserSession, "role_keys">;

const sessionStore = createSessionStore<UserSession>({
  accessTokenKey: ACCESS_TOKEN_KEY,
  userKey: USER_KEY,
});

const authSourceStore = createSessionValueStore({
  key: AUTH_SOURCE_KEY,
  allowedValues: ["local", "sso"] as const,
  defaultValue: "local",
});

function purgeLegacyAuthStorage() {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.removeItem(ACCESS_TOKEN_KEY);
  window.localStorage.removeItem(USER_KEY);
  window.localStorage.removeItem(AUTH_SOURCE_KEY);
}

purgeLegacyAuthStorage();

export const {
  getAccessToken,
  getStoredUser,
  setAccessToken,
  setSession,
  setStoredUser,
  isAuthenticated,
} = sessionStore;

export function clearSession() {
  sessionStore.clearSession();
}

export function getStoredAuthSource(): AuthLoginSource {
  return authSourceStore.get() ?? "local";
}

export function setStoredAuthSource(source: AuthLoginSource) {
  authSourceStore.set(source);
}

export function clearStoredAuthSource() {
  authSourceStore.clear();
}

export function getStoredLocale(): AppLocale {
  if (typeof window === "undefined") {
    return getStoredAppLocale();
  }
  const legacyLocale = window.localStorage.getItem(LOCALE_KEY);
  return legacyLocale ? normalizeLocale(legacyLocale) : getStoredAppLocale();
}

export function setStoredLocale(locale: string) {
  const normalized = normalizeLocale(locale);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(LOCALE_KEY, normalized);
  }
  setStoredAppLocale(normalized);
  return normalized;
}

function readRoleGateKeys(
  roleOrRoles: UserRole | UserRole[] | null | undefined,
  fieldName: string,
): UserRole[] {
  if (Array.isArray(roleOrRoles)) {
    return readUserRoleKeys({ role_keys: roleOrRoles }, fieldName);
  }
  if (!roleOrRoles) {
    return [];
  }
  return [readUserRoleKey(roleOrRoles, fieldName)];
}

export function hasPortalAccess(roleOrRoles?: UserRole | UserRole[] | null) {
  return hasAnyRole(roleOrRoles, PORTAL_ROLES);
}

export function readCurrentUserRoleKeys(
  currentUser: CurrentUserRoleSource | null | undefined,
): UserRole[] {
  if (!currentUser) {
    return [];
  }
  return readUserRoleKeys(currentUser, "currentUser.role_keys");
}

export function hasAnyRole(
  roleOrRoles: UserRole | UserRole[] | null | undefined,
  allowedRoles: UserRole[],
) {
  const roleKeys = readRoleGateKeys(roleOrRoles, "role_keys");
  const allowedRoleKeys = readUserRoleKeys({ role_keys: allowedRoles }, "allowedRoles");
  return roleKeys.some((role) => allowedRoleKeys.includes(role));
}
